// src/pages/admin/AdminDashboard.tsx
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { GlobalStyles } from "../../styles/globalStyles";
import {
  Users,
  Shield,
  FileText,
  CreditCard,
  TrendingUp,
  Clock,
  Activity,
  AlertCircle,
  CheckCircle,
  XCircle,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";

const AdminDashboard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    pendingKyc: 0,
    totalTransactions: 0,
    pendingDeposits: 0,
    pendingWithdrawals: 0,
    totalRecoveryCases: 0,
    activeRecoveryCases: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      // Simulate API call
      setTimeout(() => {
        setStats({
          totalUsers: 1254,
          activeUsers: 342,
          pendingKyc: 23,
          totalTransactions: 8456,
          pendingDeposits: 15,
          pendingWithdrawals: 8,
          totalRecoveryCases: 167,
          activeRecoveryCases: 42,
        });
      }, 1000);
    } catch (error) {
      console.error("Failed to fetch dashboard stats:", error);
      toast.error("Failed to load dashboard statistics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
            <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 p-12">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin mx-auto"></div>
                <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
              </div>
              <p className="mt-6 text-gray-600 dark:text-gray-400 font-medium">
                Loading admin dashboard...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      <div className="p-4 lg:p-5">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Admin Dashboard
            </h1>
            <Sparkles className="h-4 w-4 text-primary/60" />
          </div>
          <p className="text-gray-500 dark:text-gray-400">
            Welcome back, Admin {user?.first_name}. Here's your platform
            overview.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Users Card */}
          <Link
            to="/admin/users"
            className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-100/50 to-blue-50/50 dark:from-blue-900/20 dark:to-blue-800/10">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgePrimary} px-2 py-0.5`}
              >
                Users
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Users
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {stats.totalUsers.toLocaleString()}
            </p>
            <div className="flex items-center mt-4 pt-3 border-t border-gray-100/50 dark:border-gray-700/50">
              <Activity className="h-3 w-3 text-green-500 mr-1" />
              <span className="text-xs text-green-600 dark:text-green-400">
                {stats.activeUsers} active today
              </span>
            </div>
          </Link>

          {/* KYC Card */}
          <Link
            to="/admin/kyc"
            className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-yellow-100/50 to-yellow-50/50 dark:from-yellow-900/20 dark:to-yellow-800/10">
                <Shield className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgeWarning} px-2 py-0.5`}
              >
                Review
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Pending KYC
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {stats.pendingKyc}
            </p>
            <div className="mt-4">
              <div className="h-2 bg-gray-200/50 dark:bg-gray-700/50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500"
                  style={{ width: `${Math.min(stats.pendingKyc * 10, 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {stats.pendingKyc} submissions to review
              </p>
            </div>
          </Link>

          {/* Transactions Card */}
          <Link
            to="/admin/transactions"
            className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-green-100/50 to-green-50/50 dark:from-green-900/20 dark:to-green-800/10">
                <CreditCard className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgeSuccess} px-2 py-0.5`}
              >
                Transactions
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Transactions
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {stats.totalTransactions.toLocaleString()}
            </p>
            <div className="flex items-center mt-4 pt-3 border-t border-gray-100/50 dark:border-gray-700/50">
              <Clock className="h-3 w-3 text-yellow-500 mr-1" />
              <span className="text-xs text-yellow-600 dark:text-yellow-400">
                {stats.pendingDeposits + stats.pendingWithdrawals} pending
              </span>
            </div>
          </Link>

          {/* Recovery Card */}
          <Link
            to="/admin/recovery"
            className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-100/50 to-purple-50/50 dark:from-purple-900/20 dark:to-purple-800/10">
                <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgeInfo} px-2 py-0.5`}
              >
                Recovery
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Active Recovery
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {stats.activeRecoveryCases}
            </p>
            <div className="mt-4">
              <div className="h-2 bg-gray-200/50 dark:bg-gray-700/50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-400 to-purple-500"
                  style={{
                    width: `${Math.min(stats.activeRecoveryCases * 5, 100)}%`,
                  }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {stats.totalRecoveryCases} total cases
              </p>
            </div>
          </Link>
        </div>

        {/* Quick Actions */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-6 shadow-lg mb-8">
          <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-6">
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/admin/users"
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group/action`}
            >
              <Users className="h-4 w-4 mr-2 transition-transform group-hover/action:-translate-x-0.5" />
              Manage Users
            </Link>
            <Link
              to="/admin/kyc"
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group/action`}
            >
              <Shield className="h-4 w-4 mr-2 transition-transform group-hover/action:-translate-x-0.5" />
              Review KYC
            </Link>
            <Link
              to="/admin/recovery"
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group/action`}
            >
              <FileText className="h-4 w-4 mr-2 transition-transform group-hover/action:-translate-x-0.5" />
              Recovery Cases
            </Link>
            <Link
              to="/admin/transactions"
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start group/action`}
            >
              <CreditCard className="h-4 w-4 mr-2 transition-transform group-hover/action:-translate-x-0.5" />
              Transactions
            </Link>
          </div>
        </div>

        {/* Pending Actions */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-6 shadow-lg">
          <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-6">
            Pending Actions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-5 bg-gray-50/50 dark:bg-gray-800/50 rounded-xl hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 group">
              <div className="flex items-center justify-between mb-4">
                <span className="font-medium text-gray-900 dark:text-white">
                  Deposits to approve
                </span>
                <div className="p-1.5 rounded-lg bg-yellow-500/10">
                  <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                </div>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Pending deposit approvals requiring attention
              </p>
              <div className="flex items-center justify-between">
                <span
                  className={`${GlobalStyles.badge} ${GlobalStyles.badgeWarning} px-3 py-1`}
                >
                  {stats.pendingDeposits}
                </span>
                <Link
                  to="/admin/transactions?type=deposit&status=pending"
                  className="text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  Review →
                </Link>
              </div>
            </div>

            <div className="p-5 bg-gray-50/50 dark:bg-gray-800/50 rounded-xl hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 group">
              <div className="flex items-center justify-between mb-4">
                <span className="font-medium text-gray-900 dark:text-white">
                  Withdrawals to process
                </span>
                <div className="p-1.5 rounded-lg bg-red-500/10">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Pending withdrawal requests waiting for processing
              </p>
              <div className="flex items-center justify-between">
                <span
                  className={`${GlobalStyles.badge} ${GlobalStyles.badgeError} px-3 py-1`}
                >
                  {stats.pendingWithdrawals}
                </span>
                <Link
                  to="/admin/transactions?type=withdrawal&status=pending"
                  className="text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  Process →
                </Link>
              </div>
            </div>

            <div className="p-5 bg-gray-50/50 dark:bg-gray-800/50 rounded-xl hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 group">
              <div className="flex items-center justify-between mb-4">
                <span className="font-medium text-gray-900 dark:text-white">
                  KYC to review
                </span>
                <div className="p-1.5 rounded-lg bg-blue-500/10">
                  <CheckCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Identity verification submissions pending review
              </p>
              <div className="flex items-center justify-between">
                <span
                  className={`${GlobalStyles.badge} ${GlobalStyles.badgePrimary} px-3 py-1`}
                >
                  {stats.pendingKyc}
                </span>
                <Link
                  to="/admin/kyc"
                  className="text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  Verify →
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-6 shadow-lg mt-8">
          <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-6">
            Recent Platform Activity
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-100/50 dark:hover:bg-gray-700/50 transition-colors">
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-green-500/10 mr-4">
                  <TrendingUp className="h-4 w-4 text-green-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    12 new users registered today
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    +8% from yesterday
                  </p>
                </div>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                2 hours ago
              </span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-100/50 dark:hover:bg-gray-700/50 transition-colors">
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-red-500/10 mr-4">
                  <XCircle className="h-4 w-4 text-red-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    3 KYC submissions rejected
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Poor quality photos
                  </p>
                </div>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                4 hours ago
              </span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-100/50 dark:hover:bg-gray-700/50 transition-colors">
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-blue-500/10 mr-4">
                  <CheckCircle className="h-4 w-4 text-blue-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    8 deposits confirmed
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Total: $4,568.00
                  </p>
                </div>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                6 hours ago
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
